// src/constants/pageSEO.js

import constants from "./constants";
import globalState from "../utils/globalState";

// Cache user params to reduce repeated lookups
let cachedUserParams = null;

const getAllUserParams = () => {
  if (!cachedUserParams) {
    cachedUserParams = globalState.getAllUserParams();
  }
  return cachedUserParams;
};

const getCurrentAgentName = () => getAllUserParams().agentName;
const getCurrentAgentTitle = () => getAllUserParams().agentTitle;
const getCurrentAgentNumber = () => getAllUserParams().agentNumber;
const getCurrentAgentEmail = () => getAllUserParams().agentEmail;
const getCurrentBrokerage = () => getAllUserParams().brokerage;
const getCurrentQuote = () => getAllUserParams().quote;
const getCurrentAbout = () => getAllUserParams().about;
const getCurrentHeadshot = () => getAllUserParams().headshot;

const pageSEO = {
  // home page SEO
  home: () => ({
    title: `${getCurrentAgentName()} - ${getCurrentAgentTitle()} at ${getCurrentBrokerage()}`,
    description: `${getCurrentAbout()} ${getCurrentQuote()}`,
    keywords: `${getCurrentAgentName()}, ${getCurrentBrokerage()}, real estate, ${getCurrentAgentTitle()}, property expert`,
    path: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: getCurrentAgentName(),
      image: getCurrentHeadshot(),
      description: getCurrentAbout(),
      telephone: getCurrentAgentNumber(),
      email: getCurrentAgentEmail(),
      worksFor: {
        "@type": "RealEstateOrganization",
        name: getCurrentBrokerage(),
      },
    },
  }),

  // listings page SEO
  listings: () => ({
    title: `Property Listings | ${getCurrentAgentName()}`,
    description: `Browse our curated selection of properties with ${getCurrentAgentName()}. Find your dream home with expert guidance from ${getCurrentBrokerage()}.`,
    keywords: `property listings, real estate, ${getCurrentAgentName()}, ${getCurrentBrokerage()}, homes for sale`,
    path: "/listings",
  }),

  // listing page SEO
  listing: (listing) => ({
    // title: `${listing.address} - ${listing.location} | ${getCurrentAgentName()}`,
    title: `${listing.StreetNumber} ${listing.StreetName} ${listing.StreetSuffix} - ${listing.City} ${listing.StateOrProvince} | ${getCurrentAgentName()}`,
    description: `${listing.bedrooms} bedroom ${listing.bathrooms} bathroom ${listing.propertyType} located in ${listing.location}, priced at ${listing.price}. Contact ${getCurrentAgentName()} for more information.`,
    keywords: `${listing.location}, ${listing.propertyType}, ${listing.bedrooms} bedroom, ${listing.bathrooms} bathroom, property, ${getCurrentAgentName()}`,
    path: `/listing/${listing.id}`,
    image: listing.mainImage,
    type: "product",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing.address,
      description: listing.description,
      image: listing.mainImage,
      offers: {
        "@type": "Offer",
        priceCurrency: "CAD",
        price: listing?.price?.replace(/[^\d.]/g, ""),
        availability: "https://schema.org/InStock",
      },
      seller: {
        "@type": "RealEstateAgent",
        name: getCurrentAgentName(),
        telephone: getCurrentAgentNumber(),
        email: getCurrentAgentEmail(),
      },
    },
  }),

  // location page SEO
  location: (location) => ({
    title: `${location.name} Real Estate and Properties`,
    description: `Browse the latest properties, real estate market trends, and community information in ${location.name}. Learn about housing prices, schools, and local amenities in ${location.name}.`,
    keywords: `${location.name}, real estate, properties, homes for sale, ${location.name} community, ${location.name} housing market`,
    path: `/location/${location.slug}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Place",
      name: location.name,
      description: `Browse the latest properties, real estate market trends, and community information in ${location.name}.`,
    },
  }),

  // agents page SEO
  agents: () => ({
    title: "Our Real Estate Agents",
    description:
      "Meet our team of professional real estate agents. Each agent brings extensive experience and expertise to help you buy or sell your property.",
    keywords:
      "real estate agents, realtors, property experts, real estate professionals",
    path: "/agents",
  }),

  // agent page SEO
  agent: (agent) => ({
    title: `${agent.name} - ${agent.title}`,
    description: `Meet ${agent.name}, our professional ${agent.title}. ${
      agent.bio ? agent.bio.substring(0, 150) + "..." : ""
    }`,
    keywords: `${agent.name}, real estate agent, realtor, property expert`,
    path: `/agents/${agent.name.toLowerCase().replace(/\s+/g, "-")}`,
    image: agent.img,
    type: "profile",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: agent.name,
      image: agent.img,
      description: agent.bio,
      telephone: agent.number,
      email: agent.email,
      worksFor: {
        "@type": "RealEstateOrganization",
        name: getCurrentBrokerage(),
      },
    },
  }),

  // contact page SEO
  contact: () => ({
    title: `Contact ${getCurrentAgentName()}`,
    description: `Contact ${getCurrentAgentName()}, ${getCurrentAgentTitle()} at ${getCurrentBrokerage()} for professional real estate advice. Phone: ${getCurrentAgentNumber()}, Email: ${getCurrentAgentEmail()}`,
    keywords: `contact ${getCurrentAgentName()}, real estate agent, ${getCurrentBrokerage()}, property consultation`,
    path: "/contact",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPoint",
      telephone: getCurrentAgentNumber(),
      contactType: "customer service",
      email: getCurrentAgentEmail(),
      availableLanguage: "English",
    },
  }),

  // evaluation page SEO
  evaluation: () => ({
    title: `Property Evaluation | ${getCurrentAgentName()}`,
    description: `Get a free market evaluation of your property from ${getCurrentAgentName()} at ${getCurrentBrokerage()}. Understand your property's value in the current market.`,
    keywords: `property evaluation, home value, market analysis, real estate assessment, ${getCurrentAgentName()}`,
    path: "/evaluation",
  }),

  // calculator page SEO
  calculator: () => ({
    title: `Mortgage Calculator | ${getCurrentAgentName()}`,
    description: `Use our mortgage calculator to estimate your monthly payments and affordability. Get expert advice from ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `mortgage calculator, loan calculator, monthly payments, interest, home buying budget, ${getCurrentAgentName()}`,
    path: "/calculator",
  }),

  // reports page SEO
  reports: () => ({
    title: `Real Estate Market Reports | ${getCurrentAgentName()}`,
    description: `Latest real estate market trends and analysis from ${getCurrentAgentName()} at ${getCurrentBrokerage()}. Stay informed about housing price trends and market dynamics.`,
    keywords: `real estate market, market reports, price trends, market analysis, real estate forecast, ${getCurrentAgentName()}`,
    path: "/real-estate-reports",
  }),

  // commercial page SEO
  commercial: () => ({
    title: `Commercial Real Estate | ${getCurrentAgentName()}`,
    description: `Browse commercial property listings with ${getCurrentAgentName()}, ${getCurrentAgentTitle()} at ${getCurrentBrokerage()}. Find office, retail, and industrial opportunities.`,
    keywords: `commercial real estate, office space, retail space, investment property, ${getCurrentBrokerage()}`,
    path: "/commercial",
  }),

  // seller guide page SEO
  sellerGuide: () => ({
    title: "Seller's Guide",
    description:
      "Professional guide to selling your home, providing tips on property preparation, pricing strategy, and marketing techniques to get the best price.",
    keywords:
      "home selling, seller's guide, property sales, real estate market, pricing",
    path: "/selling-guide",
  }),

  // buyer guide page SEO
  buyerGuide: () => ({
    title: "Buyer's Guide",
    description:
      "Comprehensive guide to buying a home, helping you understand the buying process, financing options, and factors to consider.",
    keywords:
      "home buying, buyer's guide, real estate, buying process, mortgage",
    path: "/buying-guide",
  }),

  // about page SEO
  about: () => ({
    title: `About ${getCurrentAgentName()} | ${getCurrentBrokerage()}`,
    description: `Learn more about ${getCurrentAgentName()}, ${getCurrentAgentTitle()} at ${getCurrentBrokerage()}. ${getCurrentAbout()}`,
    keywords: `about ${getCurrentAgentName()}, ${getCurrentBrokerage()}, real estate agent, ${getCurrentAgentTitle()}`,
    path: "/about",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: getCurrentAgentName(),
      worksFor: {
        "@type": "RealEstateOrganization",
        name: getCurrentBrokerage(),
      },
      telephone: getCurrentAgentNumber(),
      email: getCurrentAgentEmail(),
      image: getCurrentHeadshot(),
    },
  }),
};

export default pageSEO;
